/**
 * fetch helpers for the agent backend. Every helper throws an `ApiError`
 * on non-OK responses with `status`, `code`, `message` parsed from the
 * server's `{ error, message }` envelope.
 *
 * `configureApiBase` + `getEmbedSocket` are re-exported from
 * `./socket-client` so callers have a single import point.
 */

import { configureAgentToken, configureApiBase, getAgentToken, getApiBase, getEmbedSocket } from './socket-client'

export { configureAgentToken, configureApiBase, getApiBase, getEmbedSocket }

export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  constructor({ status, code, message }: { status: number; code?: string; message: string }) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    if (code !== undefined) {
      this.code = code
    }
  }
}

async function buildApiError(res: Response): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* not JSON */
  }
  if (body && typeof body === 'object') {
    const { error, message } = body as { error?: unknown; message?: unknown }
    return new ApiError({
      status: res.status,
      ...(typeof error === 'string' ? { code: error } : {}),
      message:
        typeof message === 'string' && message.length > 0
          ? message
          : typeof error === 'string'
            ? error
            : res.statusText,
    })
  }
  return new ApiError({ status: res.status, message: res.statusText || `Request failed (${res.status})` })
}

function urlOf(path: string): string {
  const base = getApiBase()
  if (!base) {
    return path
  }
  return path.startsWith('http') ? path : `${base}${path}`
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await getAgentToken()
  return token ? { authorization: `Bearer ${token}` } : {}
}

export async function fetcher<T>(path: string): Promise<T> {
  const res = await fetch(urlOf(path), {
    credentials: 'include',
    headers: { ...(await authHeader()) },
  })
  if (!res.ok) {
    throw await buildApiError(res)
  }
  return res.json()
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(urlOf(path), {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...(await authHeader()),
      ...(init.headers ?? {}),
    },
  })
  if (!res.ok) {
    throw await buildApiError(res)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return (await res.json()) as T
}

export async function postJSON<T>(path: string, data: unknown): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(data) })
}
